import React, { useCallback, useEffect, useState } from "react";

// 관리자 · 결정례 정답지 — 세관 결정례를 AI 검수자가 다시 확인해 만든 평가·근거용 문제집(판정 로직과 무관).
// 구분: 확인된 정답(결정과 검수가 같고 현행 10자리까지 정해짐) · 바로잡은 정답(두 검수자가 같은 다른 답에 합의)
//       · 국내 실무 쟁점(검수자는 틀렸다 보나 국내 결정례가 일관되게 반대) · 제외(엇갈림·판단 불가).
const KINDS = ["확인된 정답", "바로잡은 정답", "국내 실무 쟁점", "제외", "미검수"];
const PAGE = 50;

const authHeaders = () => {
  const token = localStorage.getItem("admin_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const formatCode = (code) => {
  const v = String(code || "");
  if (v.length === 10) return `${v.slice(0, 4)}.${v.slice(4, 6)}-${v.slice(6)}`;
  if (v.length === 6) return `${v.slice(0, 4)}.${v.slice(4)}`;
  return v || "—";
};

function Stat({ label, value, active, onClick }) {
  return (
    <button type="button" onClick={onClick} style={{ ...S.stat, ...(active ? S.statOn : null) }} aria-pressed={active}>
      <span style={S.statValue}>{Number(value || 0).toLocaleString()}</span>
      <span style={S.statLabel}>{label}</span>
    </button>
  );
}

function SourceView({ API_BASE_URL, id }) {
  const [src, setSrc] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    fetch(`${API_BASE_URL}/api/onto/admin/answer-key/source?id=${encodeURIComponent(id)}`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(r.status === 404 ? "원문이 없습니다" : `HTTP ${r.status}`))))
      .then(setSrc)
      .catch((e) => setErr(String(e.message || e)));
  }, [API_BASE_URL, id]);
  if (err) return <div style={S.muted}>원문을 불러오지 못했습니다: {err}</div>;
  if (!src) return <div style={S.muted}>원문 불러오는 중…</div>;
  return (
    <div style={S.source} data-testid="answer-key-source">
      <div style={S.muted}>{[src.org, src.ref_no, src.decision_date, formatCode(src.decided_hsk)].filter(Boolean).join(" · ")}</div>
      <div style={S.sourceHead}>물품 설명</div>
      <div style={S.sourceText}>{src.description || "—"}</div>
      <div style={S.sourceHead}>결정 이유</div>
      <div style={S.sourceText}>{src.reasoning || "—"}</div>
    </div>
  );
}

const VERDICTS = ["맞음", "틀림", "보류"];

// 사람 검토 표시 — 정답지가 다시 실려도 남는다(서버에 덧붙이기만, 마지막 표시가 현재).
function ReviewBox({ API_BASE_URL, id, review, onSaved }) {
  const [verdict, setVerdict] = useState(review?.verdict || "");
  const [code, setCode] = useState(review?.corrected_hsk || "");
  const [memo, setMemo] = useState(review?.memo || "");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const save = (v) => {
    setBusy(true);
    setMsg(null);
    fetch(`${API_BASE_URL}/api/onto/admin/answer-key/review`, {
      method: "POST",
      headers: { ...authHeaders(), "Content-Type": "application/json" },
      body: JSON.stringify({ id, verdict: v || null, corrected_hsk: v === "틀림" ? code : null, memo: v ? memo : null }),
    })
      .then((r) => r.json().catch(() => ({})).then((b) => (r.ok ? b : Promise.reject(new Error(b.error || `HTTP ${r.status}`)))))
      .then((b) => {
        onSaved(b.review || null);
        if (!v) { setVerdict(""); setCode(""); setMemo(""); }
        setMsg(v ? "저장했습니다" : "표시를 지웠습니다");
      })
      .catch((e) => setMsg(`저장하지 못했습니다: ${e.message || e}`))
      .finally(() => setBusy(false));
  };
  return (
    <div style={S.review} data-testid="answer-key-review" onClick={(e) => e.stopPropagation()}>
      <b>사람 검토</b>
      <span role="group" aria-label="검토 표시" style={{ display: "inline-flex", gap: 4 }}>
        {VERDICTS.map((v) => (
          <button key={v} type="button" aria-pressed={verdict === v} onClick={() => setVerdict(v)}
            style={{ ...S.btn, ...(verdict === v ? S.btnOn : null) }}>{v}</button>
        ))}
      </span>
      {verdict === "틀림" && (
        <input aria-label="바른 세번" value={code} onChange={(e) => setCode(e.target.value)} placeholder="바른 세번(6·10자리, 선택)" style={{ ...S.input, width: 170 }} />
      )}
      <input aria-label="메모" value={memo} onChange={(e) => setMemo(e.target.value)} placeholder="메모(선택)" style={{ ...S.input, flex: "1 1 200px" }} />
      <button type="button" style={S.btn} disabled={busy || !verdict} onClick={() => save(verdict)}>저장</button>
      {review && <button type="button" style={S.btn} disabled={busy} onClick={() => save(null)}>표시 지우기</button>}
      {msg && <span style={S.muted}>{msg}</span>}
      {review && <span style={S.muted}>현재: {review.verdict}{review.corrected_hsk ? ` → ${formatCode(review.corrected_hsk)}` : ""} · {review.reviewer} · {String(review.reviewed_at || "").slice(0, 16).replace("T", " ")}</span>}
    </div>
  );
}

function Row({ it, API_BASE_URL }) {
  const [open, setOpen] = useState(false);
  const [showSource, setShowSource] = useState(false);
  const [review, setReview] = useState(it.review || null);
  const differs = it.answer_hsk && it.decided_hsk && it.answer_hsk !== it.decided_hsk;
  return (
    <>
      <tr style={S.tr} onClick={() => setOpen((v) => !v)} aria-expanded={open}>
        <td style={S.td}><span style={S.kind}>{it.kind}</span></td>
        <td style={{ ...S.td, ...S.mono }}>{it.id}</td>
        <td style={S.td}>{it.decision_date || "—"}</td>
        <td style={{ ...S.td, ...S.mono }}>{formatCode(it.decided_hsk)}</td>
        <td style={{ ...S.td, ...S.mono, fontWeight: 700, color: differs ? "#1d4ed8" : undefined }}>
          {formatCode(it.answer_hsk || it.answer_hs6)}
        </td>
        <td style={S.td}>{it.confidence || "—"}</td>
        <td style={{ ...S.td, whiteSpace: "nowrap", fontWeight: review?.verdict === "틀림" ? 700 : undefined }}>{review?.verdict || ""}</td>
        <td style={{ ...S.td, maxWidth: 420 }}>
          <div style={S.clip}>{it.input_ko || it.title || "—"}</div>
        </td>
      </tr>
      {open && (
        <tr>
          <td colSpan={8} style={S.detail}>
            <div><b>제목</b> {it.title || "—"}</div>
            <div><b>정리한 입력</b> {it.input_ko || "—"} {it.input_sufficient === false && <span style={S.warn}>(입력만으로는 부족)</span>}</div>
            {Array.isArray(it.missing_facts) && it.missing_facts.length > 0 && <div><b>모자라는 사실</b> {it.missing_facts.join(" · ")}</div>}
            {Array.isArray(it.issues) && it.issues.length > 0 && <div><b>문제 표시</b> {it.issues.join(" · ")}</div>}
            {it.note && <div><b>검수 의견</b> {it.note}</div>}
            {it.cross_verdict && <div><b>교차 검수</b> {it.cross_verdict} — {it.cross_reason || ""}</div>}
            <div style={{ margin: "4px 0" }}>
              <button type="button" style={S.btn} aria-expanded={showSource} onClick={(e) => { e.stopPropagation(); setShowSource((v) => !v); }}>
                {showSource ? "원문 닫기" : "원문 보기"}
              </button>
            </div>
            {showSource && <SourceView API_BASE_URL={API_BASE_URL} id={it.id} />}
            <ReviewBox API_BASE_URL={API_BASE_URL} id={it.id} review={review} onSaved={setReview} />
            <div style={S.muted}>회차 {it.round} · 류 {it.chapter || "—"}
              {it.decision_url && <> · <a href={it.decision_url.startsWith("http") ? it.decision_url : `https://unipass.customs.go.kr${it.decision_url}`} target="_blank" rel="noopener noreferrer">결정례 원문 ↗</a></>}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export default function AdminAnswerKey({ API_BASE_URL = "" }) {
  const [summary, setSummary] = useState(null);
  const [kind, setKind] = useState("확인된 정답");
  const [chapter, setChapter] = useState("");
  const [q, setQ] = useState("");
  const [round, setRound] = useState("");
  const [confidence, setConfidence] = useState("");
  const [differs, setDiffers] = useState(false);
  const [reviewFilter, setReviewFilter] = useState("");
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    fetch(`${API_BASE_URL}/api/onto/admin/answer-key`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then(setSummary)
      .catch((e) => setErr(String(e.message || e)));
  }, [API_BASE_URL]);

  const load = useCallback(() => {
    const p = new URLSearchParams({ offset: String(offset), limit: String(PAGE) });
    if (kind) p.set("kind", kind);
    if (chapter.trim()) p.set("chapter", chapter.trim());
    if (q.trim()) p.set("q", q.trim());
    if (String(round).trim()) p.set("round", String(round).trim());
    if (confidence) p.set("confidence", confidence);
    if (differs) p.set("differs", "true");
    if (reviewFilter) p.set("review", reviewFilter);
    fetch(`${API_BASE_URL}/api/onto/admin/answer-key/items?${p}`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((d) => { setData(d); setErr(null); })
      .catch((e) => setErr(String(e.message || e)));
  }, [API_BASE_URL, kind, chapter, q, offset, round, confidence, differs, reviewFilter]);

  useEffect(() => { load(); }, [load]);

  const byKind = summary?.by_kind || {};
  const total = data?.total ?? 0;
  return (
    <div style={S.wrap}>
      <div>
        <h2 style={S.h2}>결정례 정답지</h2>
        <p style={S.lead}>
          세관 결정례를 AI 검수자가 다시 확인해 만든 채점용 문제집입니다. 판정 로직에는 쓰지 않습니다.
          '미검수'는 검수 대상 밖(중복 수집·2002년 이전 등)이라 검색용으로만 올렸고, 채점용으로 떼어 둔 봉인 20%는 올리지 않았습니다.
          {summary?.loaded_at && <> 마지막 적재 {summary.loaded_at.slice(0, 16).replace("T", " ")} · 검수 {Number(summary.total || 0).toLocaleString()}건</>}
        </p>
      </div>
      <div style={S.stats}>
        {KINDS.map((k) => (
          <Stat key={k} label={k} value={byKind[k]} active={kind === k} onClick={() => { setKind(kind === k ? "" : k); setOffset(0); }} />
        ))}
      </div>
      <form style={S.filters} onSubmit={(e) => { e.preventDefault(); setOffset(0); load(); }}>
        <label style={S.label}>류 <input aria-label="류" value={chapter} onChange={(e) => setChapter(e.target.value)} placeholder="예: 85" style={{ ...S.input, width: 70 }} /></label>
        <label style={S.label}>회차 <input aria-label="회차" value={round} onChange={(e) => setRound(e.target.value.replace(/\D/g, ""))} placeholder="예: 12" style={{ ...S.input, width: 60 }} /></label>
        <label style={S.label}>확신
          <select aria-label="확신" value={confidence} onChange={(e) => { setConfidence(e.target.value); setOffset(0); }} style={S.input}>
            <option value="">전체</option>
            <option value="high">높음</option>
            <option value="medium">중간</option>
            <option value="low">낮음</option>
          </select>
        </label>
        <label style={S.label}>
          <input type="checkbox" aria-label="세관 결정과 다른 것만" checked={differs} onChange={(e) => { setDiffers(e.target.checked); setOffset(0); }} />
          세관 결정과 다른 것만
        </label>
        <label style={S.label}>사람 검토
          <select aria-label="사람 검토" value={reviewFilter} onChange={(e) => { setReviewFilter(e.target.value); setOffset(0); }} style={S.input}>
            <option value="">전체</option>
            <option value="미표시">미표시{summary?.by_review ? ` (${Number(summary.by_review["미표시"] || 0).toLocaleString()})` : ""}</option>
            {VERDICTS.map((v) => (
              <option key={v} value={v}>{v}{summary?.by_review ? ` (${Number(summary.by_review[v] || 0).toLocaleString()})` : ""}</option>
            ))}
          </select>
        </label>
        <label style={S.label}>검색 <input aria-label="검색" value={q} onChange={(e) => setQ(e.target.value)} placeholder="품명·결정례 번호·세번" style={{ ...S.input, width: 240 }} /></label>
        <button type="submit" style={S.btn}>찾기</button>
        <span style={S.muted}>{total.toLocaleString()}건</span>
      </form>
      {err && <div style={S.err}>불러오지 못했습니다: {err}</div>}
      <div style={{ overflowX: "auto" }}>
        <table style={S.table}>
          <thead>
            <tr>
              <th style={S.th}>구분</th><th style={S.th}>결정례</th><th style={S.th}>결정일</th>
              <th style={S.th}>세관 결정</th><th style={S.th}>정답</th><th style={S.th}>확신</th><th style={S.th}>검토</th><th style={S.th}>정리한 입력</th>
            </tr>
          </thead>
          <tbody>{(data?.items || []).map((it) => <Row key={it.id} it={it} API_BASE_URL={API_BASE_URL} />)}</tbody>
        </table>
      </div>
      <div style={S.pager}>
        <button type="button" style={S.btn} disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}>이전</button>
        <span style={S.muted}>{total ? `${offset + 1}–${Math.min(offset + PAGE, total)} / ${total.toLocaleString()}` : "—"}</span>
        <button type="button" style={S.btn} disabled={offset + PAGE >= total} onClick={() => setOffset(offset + PAGE)}>다음</button>
      </div>
    </div>
  );
}

const S = {
  wrap: { display: "flex", flexDirection: "column", gap: 12, padding: 16, fontSize: 13, color: "#1f2937" },
  h2: { margin: 0, fontSize: 18 },
  lead: { margin: "4px 0 0", color: "#6b7280", fontSize: 12.5 },
  stats: { display: "flex", gap: 8, flexWrap: "wrap" },
  stat: { display: "flex", flexDirection: "column", alignItems: "flex-start", border: "1px solid #d9dee6", borderRadius: 6, padding: "6px 12px", background: "#fff", cursor: "pointer", minWidth: 120 },
  statOn: { borderColor: "#1d4ed8", background: "#eef4fc" },
  statValue: { fontSize: 18, fontWeight: 700, fontVariantNumeric: "tabular-nums" },
  statLabel: { fontSize: 12, color: "#6b7280" },
  filters: { display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" },
  label: { display: "inline-flex", gap: 6, alignItems: "center", color: "#6b7280", fontSize: 12.5 },
  input: { border: "1px solid #d9dee6", borderRadius: 4, padding: "3px 6px", font: "inherit" },
  btn: { border: "1px solid #d9dee6", borderRadius: 4, padding: "3px 10px", background: "#fff", color: "#1d4ed8", cursor: "pointer", font: "inherit" },
  table: { borderCollapse: "collapse", width: "100%", minWidth: 900 },
  th: { textAlign: "left", fontSize: 12, color: "#6b7280", borderBottom: "1px solid #d9dee6", padding: "4px 8px", whiteSpace: "nowrap" },
  tr: { cursor: "pointer", borderBottom: "1px solid #eef0f3" },
  td: { padding: "4px 8px", verticalAlign: "top" },
  mono: { fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" },
  kind: { fontSize: 11.5, border: "1px solid #d9dee6", borderRadius: 3, padding: "0 5px", whiteSpace: "nowrap" },
  clip: { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  detail: { background: "#f7f9fc", padding: "8px 12px", lineHeight: 1.7, fontSize: 12.5, borderBottom: "1px solid #d9dee6" },
  muted: { color: "#6b7280", fontSize: 12 },
  warn: { color: "#6b7280" },
  err: { color: "#1d4ed8", fontSize: 12.5 },
  source: { border: "1px solid #d9dee6", borderRadius: 4, background: "#fff", padding: "6px 10px", margin: "4px 0", maxHeight: 360, overflowY: "auto" },
  sourceHead: { fontWeight: 700, fontSize: 12, color: "#374151", marginTop: 6 },
  sourceText: { whiteSpace: "pre-wrap", lineHeight: 1.65 },
  pager: { display: "flex", gap: 10, alignItems: "center" },
  btnOn: { borderColor: "#1d4ed8", background: "#eef4fc", fontWeight: 700 },
  review: { display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", margin: "6px 0", paddingTop: 6, borderTop: "1px solid #e5e9f0" },
};
